import { toast } from 'react-toastify'
import { useState, useEffect } from 'react'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams, postMPDataWithParams, putMPDataWithParams } from 'src/mentor/services'
import { appendHtmlContent } from 'src/consumer/helpers/utilFunctions.jsx'

export function usePatientHistoryNotes(patientId, screeningReportId, bookedEventId) {
  const [patientHistoryData, setPatientHistoryData] = useState(null)

  const [allPatientHistoryData, setAllPatientHistoryData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  async function fetchPatientHistoryNotes() {
    if (!patientId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resp = await getMPDataWithParams(`/api/secure/doctor/patient-history/of-patient`, {
        patientId: patientId,
      })
      
      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          const notesList = respData?.notes || []
          
          if (notesList.length > 0) {
            // Helper function to check if a note is blank (empty or whitespace only)
            const isNoteBlank = (note) => {
              if (!note) return true
              
              // Get content from various possible structures
              let content = ''
              if (note.content?.content) {
                content = note.content.content
              } else if (typeof note.content === 'string') {
                content = note.content
              } else if (note.note) {
                content = note.note
              } else if (note.text) {
                content = note.text
              }
              
              if (!content || typeof content !== 'string') return true
              
              // Remove HTML tags and check if there's actual text content
              const textContent = content
                .replace(/<[^>]*>/g, '') // Remove HTML tags
                .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
                .replace(/&amp;/g, '&') // Replace &amp; with &
                .replace(/&lt;/g, '<') // Replace &lt; with <
                .replace(/&gt;/g, '>') // Replace &gt; with >
                .replace(/&quot;/g, '"') // Replace &quot; with "
                .trim() // Remove leading/trailing whitespace
              
              return textContent.length === 0
            }

            // Helper function to normalize note structure
            const normalizeNoteStructure = (note) => {
              const normalized = { ...note } // Create a copy to avoid mutating original
              
              // Ensure the note has the correct structure: content.content
              if (!normalized.content) {
                // If note doesn't have content structure, create it from other possible fields
                normalized.content = {
                  content: normalized.note || normalized.text || ''
                }
              } else if (typeof normalized.content === 'string') {
                // If content is a string, wrap it in the expected structure
                normalized.content = {
                  content: normalized.content
                }
              } else if (normalized.content && typeof normalized.content === 'object' && !normalized.content.content) {
                // If content exists but doesn't have nested content, create it
                normalized.content = {
                  content: normalized.content.note || normalized.content.text || JSON.stringify(normalized.content) || ''
                }
              }
              
              return normalized
            }

            // Sort notes by date (latest first) - using createdAt or createdTime
            const sortedNotes = [...notesList].sort((a, b) => {
              const dateA = new Date(a?.createdAt || a?.createdTime || a?.createdTimeDate || 0).getTime()
              const dateB = new Date(b?.createdAt || b?.createdTime || b?.createdTimeDate || 0).getTime()
              return dateB - dateA // Descending order (latest first)
            })

            // Normalize all notes first
            const normalizedNotes = sortedNotes.map(note => normalizeNoteStructure(note))
            
            // Filter out blank notes
            const nonBlankNotes = normalizedNotes.filter(note => !isNoteBlank(note))
            
            if (nonBlankNotes.length > 0) {
              // Get the first non-blank note (latest non-blank) for the editor
              const latestNote = nonBlankNotes[0]
              const latestNoteId = latestNote?.id
              
              // Get all other non-blank notes (excluding the latest) for accordions
              // IMPORTANT: Only show previous non-blank notes in accordions, latest goes to editor
              const previousNotes = nonBlankNotes
                .slice(1) // Skip the first (latest) note
                .filter(note => note?.id !== latestNoteId) // Extra safeguard: filter by ID

              // Set the latest non-blank note for the editor (this should be editable)
              setPatientHistoryData(latestNote)
              // Set all other non-blank notes for accordions (these are read-only)
              setAllPatientHistoryData(previousNotes)
            } else {
              // No non-blank notes found, reset to null
              setPatientHistoryData(null)
              setAllPatientHistoryData([])
            }
          } else {
            // No notes found, reset to null
            setPatientHistoryData(null)
            setAllPatientHistoryData([])
          }
        } else {
          setError('Failed to fetch patient history notes')
          setPatientHistoryData(null)
          setAllPatientHistoryData([])
        }
      } else {
        setError(`API returned status ${resp.status}`)
        setPatientHistoryData(null)
        setAllPatientHistoryData([])
      }
    } catch (err) {
      console.error('Error fetching patient history notes:', err)
      setError(err.message)
      setPatientHistoryData(null)
      setAllPatientHistoryData([])
    } finally {
      setLoading(false)
    }
  }

  function importPatientHistoryNotesFromScreeningReport(screeningReportData) {
    setPatientHistoryData((prev) => {
      const existingContent = prev?.content?.content || ''
      let newContent = !!screeningReportData?.screeningContent?.content
        ? screeningReportData?.screeningContent?.content
        : screeningReportData?.content
      if (screeningReportData?.observation) {
        newContent = appendHtmlContent(
          newContent,
          `<p><b>Observations:</b> <br></br>${screeningReportData?.observation}</p>`,
          false,
        )
      }
      if (screeningReportData?.advice) {
        newContent = appendHtmlContent(
          newContent,
          `<p><b>Advices:</b> <br></br>${screeningReportData?.advice}</p>`,
          false,
        )
      }
      console.log({ screeningReportData })
      const combinedContent = appendHtmlContent(existingContent, newContent, false)
      return {
        ...prev,
        content: { ...prev?.content, content: combinedContent },
      }
    })
  }

  useEffect(() => {
    if (!patientId) {
      return
    }

    fetchPatientHistoryNotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, bookedEventId])

  const updatePatientHistoryData = async (updatedHistoryItem) => {
    setSaving(true)
    if (!updatedHistoryItem.id) {
      console.error('History item ID is required for update')
      return
    }

    try {
      const resp = await putMPDataWithParams(
        `/api/secure/doctor/patient-history/update/${updatedHistoryItem.id}`,
        updatedHistoryItem?.content || {},
      )
      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          fetchPatientHistoryNotes()
        } else {
          console.error('Api Faliure:updating patient history:', resp.data?.message)
          toast.error(resp.data.message)
          throw new Error(resp.data.message)
        }
      }
    } catch (err) {
      setError(err.message)
      console.error('Error updating patient history:', err)
      toast.error(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }
  const createPatientHistoryData = async (updatedHistoryItem, screeningReportId, bookedEventId) => {
    setSaving(true)

    try {
      const resp = await postMPDataWithParams(
        `/api/secure/doctor/patient-history/create/${bookedEventId}`,
        updatedHistoryItem?.content || {},
      )
      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          fetchPatientHistoryNotes()
        } else {
          console.error('Api Faliure:updating patient history:', resp.data?.message)
          toast.error(resp.data.message)
          throw new Error(resp.data.message)
        }
      }
    } catch (err) {
      setError(err.message)
      console.error('Error updating patient history:', err)
      toast.error(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }

  async function updateOrCreatePatientHistoryData(
    updatedHistoryItem,
    screeningReportId,
    bookedEventId,
  ) {
    const createOrUpdate = updatedHistoryItem.id
      ? updatePatientHistoryData
      : createPatientHistoryData

    return await createOrUpdate(updatedHistoryItem, screeningReportId, bookedEventId)
  }

  return {
    patientHistoryData,
    setPatientHistoryData,
    allPatientHistoryData,

    loading,
    error,
    savingRecord: saving,

    updateOrCreatePatientHistoryData,
    importPatientHistoryNotesFromScreeningReport,
    fetchPatientHistoryNotes,
  }
}

// // Mock patient history data that extends the screening report structure
// const mockPatientHistoryData = [
//   {
//     id: 'patient-history-001',
//     mentorId: 'mentor-001',
//     mentorName: 'Dr. Previous Screener',
//     patientId: 'patient-001',
//     patientNumber: 'P001',
//     bookedEventId: 'event-001',
//     relatedConsultationBookedEventId: 'consultation-001',
//     requestingDoctorId: 'doctor-001',
//     content: 'Latest patient history entry - Patient showed improvement in anxiety symptoms.',
//     patientName: 'John Doe',
//     patientAge: '35',
//     patientGender: 'Male',
//     versionNumber: 1,
//     enabled: true,
//     deleted: false,
//     patientDateOfBirth: '1988-05-15T00:00:00.000Z',
//     createdAt: '2024-01-15T10:30:00.000Z',
//     screeningContent: {},
//     observation:
//       'Current observation - Patient shows signs of reduced anxiety and better sleep patterns.',
//     advice: 'Continue with current treatment plan and regular follow-ups.',
//     complaints: [
//       {
//         id: 'complaint-001',
//         name: 'Anxiety',
//         description: 'Patient reported feeling less anxious',
//       },
//     ],
//     diagnosis: [
//       {
//         id: 'diagnosis-001',
//         name: 'Generalized Anxiety Disorder',
//         description: 'Improving symptoms',
//       },
//     ],
//     editLog: [],
//     screeningAnswers: [
//       {
//         questionId: 'q1',
//         answer: 'Sometimes',
//         score: 2,
//       },
//     ],
//     suggestedBookingTherapy: false,
//     requestingDoctorName: 'Dr. Smith',
//   },
//   {
//     id: 'patient-history-002',
//     mentorId: 'mentor-002',
//     mentorName: 'Dr. Another Screener',
//     patientId: 'patient-001',
//     patientNumber: 'P001',
//     bookedEventId: 'event-002',
//     relatedConsultationBookedEventId: 'consultation-002',
//     requestingDoctorId: 'doctor-001',
//     content: 'Previous patient history entry - Initial assessment and treatment plan.',
//     patientName: 'John Doe',
//     patientAge: '35',
//     patientGender: 'Male',
//     versionNumber: 1,
//     enabled: true,
//     deleted: false,
//     patientDateOfBirth: '1988-05-15T00:00:00.000Z',
//     createdAt: '2023-12-10T14:20:00.000Z',
//     screeningContent: {},
//     observation: 'Initial observation - Patient showed signs of anxiety and stress.',
//     advice: 'Recommended regular exercise and stress management techniques.',
//     complaints: [
//       {
//         id: 'complaint-001',
//         name: 'Anxiety',
//         description: 'Patient reported feeling anxious frequently',
//       },
//       {
//         id: 'complaint-002',
//         name: 'Sleep Issues',
//         description: 'Difficulty falling asleep and staying asleep',
//       },
//     ],
//     diagnosis: [
//       {
//         id: 'diagnosis-001',
//         name: 'Generalized Anxiety Disorder',
//         description: 'Mild to moderate anxiety symptoms',
//       },
//     ],
//     editLog: [],
//     screeningAnswers: [
//       {
//         questionId: 'q1',
//         answer: 'Yes',
//         score: 3,
//       },
//       {
//         questionId: 'q2',
//         answer: 'Sometimes',
//         score: 2,
//       },
//     ],
//     suggestedBookingTherapy: true,
//     requestingDoctorName: 'Dr. Smith',
//   },
// ]

// // Empty patient history template for new creation
// const createEmptyPatientHistory = (patientId, screeningReportData = {}) => {
//   return {
//     id: null, // Will be assigned by backend
//     mentorId: screeningReportData.mentorId || '',
//     mentorName: screeningReportData.mentorName || '',
//     patientId: patientId,
//     patientNumber: screeningReportData.patientNumber || '',
//     bookedEventId: screeningReportData.bookedEventId || '',
//     relatedConsultationBookedEventId: screeningReportData.relatedConsultationBookedEventId || '',
//     requestingDoctorId: screeningReportData.requestingDoctorId || '',
//     content: '',
//     patientName: screeningReportData.patientName || '',
//     patientAge: screeningReportData.patientAge || '',
//     patientGender: screeningReportData.patientGender || '',
//     versionNumber: 1,
//     enabled: true,
//     deleted: false,
//     patientDateOfBirth: screeningReportData.patientDateOfBirth || null,
//     createdAt: new Date().toISOString(),
//     screeningContent: {},
//     observation: '',
//     advice: '',
//     complaints: [],
//     diagnosis: [],
//     editLog: [],
//     screeningAnswers: [],
//     suggestedBookingTherapy: false,
//     requestingDoctorName: screeningReportData.requestingDoctorName || '',
//   }
// }

// const createPatientHistoryData = async (screeningReportData = {}) => {
//   if (!patientId) {
//     console.error('Patient ID is required to create history')
//     return
//   }

//   setLoading(true)
//   setError(null)

//   try {
//     const emptyHistory = createEmptyPatientHistory(patientId, screeningReportData)

//     // TODO: Replace with actual API call
//     // const response = await fetch(`/api/patients/${patientId}/history`, {
//     //   method: 'POST',
//     //   headers: {
//     //     'Content-Type': 'application/json',
//     //   },
//     //   body: JSON.stringify(emptyHistory)
//     // })
//     // const createdHistory = await response.json()

//     // Simulate API delay
//     await new Promise((resolve) => setTimeout(resolve, 1000))

//     // Mock created history with an ID
//     const createdHistory = {
//       ...emptyHistory,
//       id: `patient-history-${Date.now()}`,
//       createdAt: new Date().toISOString(),
//     }

//     // Set as array with the new history item
//     setPatientHistoryData([createdHistory])

//     return createdHistory
//   } catch (err) {
//     setError(err.message)
//     console.error('Error creating patient history:', err)
//     throw err
//   } finally {
//     setLoading(false)
//   }
// }

// const fetchPatientHistory = async () => {
//   setLoading(true)
//   setError(null)

//   try {
//     // TODO: Replace with actual API call
//     // const response = await fetch(`/api/patients/${patientId}/history`)
//     // if (response.status === 404) {
//     //   setPatientHistoryData(null)
//     //   return
//     // }
//     // const data = await response.json()

//     // Simulate API delay
//     await new Promise((resolve) => setTimeout(resolve, 1000))

//     // Simulate 70% chance of history existing for demo purposes
//     const hasHistory = Math.random() > 0.3

//     if (hasHistory) {
//       setPatientHistoryData(mockPatientHistoryData)
//     } else {
//       setPatientHistoryData(null)
//     }
//   } catch (err) {
//     setError(err.message)
//     console.error('Error fetching patient history:', err)
//   } finally {
//     setLoading(false)
//   }
// }
